import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Icon from '@material-ui/core/Icon';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
import { TooltipButton } from '../../../components/tooltip-button';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { actions as driverActions, selectors as driverSelectors } from '../../../redux/reducers/drivers';
// Ressources
// import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import ObjectUtils from '../../../utils/objectUtils';
import { AccountRight } from '../../../utils/userUtils';
import { BookingModule, DispatchGroup } from '../../../redux/reducers/api-types';
import { generateOptionsString, getDispatchGroupOptions } from '../../../utils/businessUtils';
// Style
import styles from './bookings-list-style';
import StringUtils from '../../../utils/stringUtils';
import { getFindUsersURL } from '../../../utils/core-api';
import { FormatType, HeadCell, PickerOption } from '../../../local-types';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

const queryString = require('query-string');

interface BookingsListProps extends WithTranslation, WithStyles, RouteComponentProps {
    dispatchGroups: DispatchGroup[];
    getDispatchGroups: () => void;
}

class BookingsListPage extends React.Component<BookingsListProps> {
    filterParams: any;
    constructor(props: BookingsListProps) {
        super(props);

        // const date = new Date();
        // const previousWeek = new Date(date.getTime() - 14 * 24 * 60 * 60 * 1000);
        // const previousWeekFormat = DateUtils.dateToString(previousWeek);

        this.filterParams = ObjectUtils.isEmpty(queryString.parse(this.props.location.search))
            ? {
                  //   'after[creation_date]': previousWeekFormat,
              }
            : queryString.parse(this.props.location.search);

        const { dispatchGroups } = this.props;
        if (dispatchGroups.length === 0) {
            this.props.getDispatchGroups();
        }
    }

    getStatusOptions = (): PickerOption[] => {
        return generateOptionsString(
            this.props.t,
            ['created', 'accepted', 'driver_waiting', 'ride_started', 'ride_ended', 'cancelled'],
            'booking_status_',
        );
    };

    headerBuilder = (): HeadCell[] => {
        const { t, dispatchGroups } = this.props;
        const headers: HeadCell[] = [
            createCell(t, Cell.ID, { sortable: true }),
            { id: 'number', label: t('common:form.number'), sortable: false, contains: true, minLength: 4 },
            {
                id: 'rider.fullname',
                label: t('rider'),
                formatCell: function (row) {
                    return (
                        <>
                            <div style={{ fontWeight: 600 }}>{row.rider.fullname}</div>
                        </>
                    );
                },
            },
            createCell(t, Cell.COMPANY, {
                id: 'company.name',
                sortable: true,
                filterable: true,
                formatCell: function (row) {
                    return (
                        <>
                            {row.company?.name ? (
                                <div style={{ marginLeft: 8, fontStyle: 'italic' }}>{row.company.name}</div>
                            ) : null}
                        </>
                    );
                },
            }),
            { id: 'driver.fullname', label: t('driver') },
            {
                id: 'status',
                label: t('status'),
                type: FormatType.PickerMultiple,
                contains: false,
                pickerOptions: this.getStatusOptions(),
                formatCell: function (row) {
                    let translation = t('booking:booking_status_' + row.status);
                    if (row.cancellation_reason) {
                        translation += ' - ' + t('booking:' + row.cancellation_reason);
                    }
                    return translation;
                },
            },
            createCell(t, Cell.CREATION_DATE),
            {
                id: 'reservation.planned_start_date',
                label: t('reservation_date'),
                type: FormatType.DateLong,
                filterable: false,
                interval: true,
                intervalDateTime: true,
                formatCell: function (row) {
                    return `${
                        row.reservation
                            ? FormattingUtils.format(row.reservation.planned_start_date, FormatType.DateLong, t)
                            : ''
                    }`;
                },
            },
            {
                id: 'pickup_location',
                label: t('pickup_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'planned_dropoff_location',
                label: t('planned_dropoff_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'bills_total_net_price_without_vat',
                label: t('prix_ttc'),
                filterable: false,
                sortable: false,
                type: FormatType.Price,
                formatCell: function (row: BookingModule) {
                    let tooltip;
                    let redeem = null;
                    let visiblePrice;
                    if (row.ride_billing) {
                        const { pre_tax_customer, pre_tax_partner, with_tax_customer, with_tax_partner } =
                            row.ride_billing;
                        tooltip = (
                            <>
                                {t('partner_ht')} : {FormattingUtils.price(pre_tax_partner, true)} <br />
                                {t('partner_ttc')} : {FormattingUtils.price(with_tax_partner, true)} <br />
                                {t('prix_ht')} : {FormattingUtils.price(pre_tax_customer, true)}
                                <br />
                                {t('prix_ttc')} : {FormattingUtils.price(with_tax_customer, true)}
                            </>
                        );
                        visiblePrice = with_tax_customer;
                    } else if (row.order) {
                        const { base_price_with_vat, base_price_without_vat } = row.order;
                        tooltip = (
                            <>
                                {t('prix_ht')} : {FormattingUtils.price(base_price_without_vat, true)}
                                <br />
                                {t('prix_ttc')} : {FormattingUtils.price(base_price_with_vat, true)}
                            </>
                        );
                        visiblePrice = base_price_with_vat;
                    } else {
                        tooltip = (
                            <>
                                {t('base_ht')} : {FormattingUtils.price(row.bills_total_base_price_without_vat, true)}{' '}
                                <br />
                                {t('base_ttc')} : {FormattingUtils.price(row.bills_total_base_price_with_vat, true)}{' '}
                                <br />
                                {t('prix_net_ht')} :{' '}
                                {FormattingUtils.price(row.bills_total_net_price_without_vat, true)}
                            </>
                        );
                        redeem = row.bills_total_net_price_without_vat !== row.bills_total_base_price_without_vat && (
                            <Icon style={{ marginLeft: 8 }}>redeem</Icon>
                        );

                        visiblePrice = row.bills_total_base_price_with_vat;
                    }

                    return (
                        <TooltipButton title={tooltip}>
                            <div style={{ alignItems: 'center', display: 'flex' }}>
                                {`${FormattingUtils.price(visiblePrice, true)}`}
                                {redeem}
                            </div>
                        </TooltipButton>
                    );
                },
            },
            {
                id: 'dispatch_group.id',
                label: t('dispatch_group'),
                filterable: dispatchGroups.length > 0,
                type: FormatType.Picker,
                pickerOptions: getDispatchGroupOptions(dispatchGroups),
                formatCell: function (row) {
                    return `${row.dispatch_group?.name ?? ''}`;
                },
            },
            // {
            //     id: 'driver_rating',
            //     label: t('driver_rating'),
            //     filterable: false,
            //     formatCell: function (row) {
            //         return `${row.driver_rating > 0 ? row.driver_rating : ''}`;
            //     },
            // },
            {
                id: 'company.account_manager.id',
                label: t('account_manager'),
                sortable: false,
                type: FormatType.Autocomplete,
                api: getFindUsersURL(),
                customAlt: (item) => item?.id,
                formatCell: function (row) {
                    const { company } = row;
                    return StringUtils.userFullname(company?.account_manager);
                },
            },
            { id: 'reason', label: t('ride_reason'), sortable: false },
            {
                id: 'reference',
                label: t('external_reference'),
                sortable: false,
                contains: true,
                minLength: 3,
                defaultVisibility: false,
            },
        ];
        return headers;
    };

    render() {
        const { t } = this.props;

        return (
            <App
                disableBreadcrumb
                needRights={AccountRight.RIDE_VIEW}
                header={{
                    title: t('title'),
                    subtitle: t('description'),
                }}
            >
                <EnhancedTable
                    headers={this.headerBuilder()}
                    type={tableRequestPoints.BOOKINGS}
                    filterParams={this.filterParams}
                />
            </App>
        );
    }
}

const mapStateToProps = (state) => ({
    dispatchGroups: driverSelectors.sortedDispatchGroup(state),
});

const mapDispatchToProps = {
    getDispatchGroups: driverActions.getDispatchGroups,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(withTranslation(['booking', 'common'])(BookingsListPage)));
